import { EventTypeBackend, EventUnion } from '../../../types/events.types';

/**
 * A terminated stakeholder's persisted issuance row is immutable: their reverse-vesting position
 * was settled at termination, so changing the shares, class, or schedule afterwards would rewrite
 * that settlement. The freeze is keyed to the stakeholder the row was saved with — a new row, or a
 * persisted row re-pointed at a terminated stakeholder mid-edit, stays editable because it is a
 * fresh issuance (allowed without reverse vesting), not part of the settled history.
 */
export const isTerminatedStakeholderIssuanceRowFrozen = (row: {
  id?: string;
  initialStakeholderId?: string;
  stakeholder?: { id?: string; isTerminated?: boolean };
}): boolean =>
  Boolean(
    row.id &&
      row.initialStakeholderId &&
      row.stakeholder?.isTerminated &&
      row.stakeholder.id === row.initialStakeholderId,
  );

/**
 * Deleting an issuance-bearing event that includes a terminated stakeholder would retroactively
 * change the holdings their termination was settled against, so the whole event is undeletable.
 * Only Share Issuance and Company Incorporation carry these rows — other event types are exempt.
 */
export const eventHasTerminatedIssuanceStakeholder = (event: EventUnion): boolean => {
  if (
    event.type !== EventTypeBackend.SHARE_ISSUANCE &&
    event.type !== EventTypeBackend.COMPANY_INCORPORATION
  ) {
    return false;
  }

  return event.items.some(({ stakeholder }) => Boolean(stakeholder?.isTerminated));
};
